import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Download, Settings, TrendingUp, Monitor, Server, CheckCircle,
  PlayCircle, KeyRound, Clock, Sliders, ToggleRight,
} from "lucide-react";

export default function HowItWorks() {
  const steps = [
    {
      icon: <Download className="w-8 h-8 text-[#00E676]" />,
      title: "Step 1: Subscribe & Sign Up",
      description: "Choose your plan, create your account, and get access to the Alphabot dashboard.",
    },
    {
      icon: <KeyRound className="w-8 h-8 text-[#007AFF]" />,
      title: "Step 2: Link Your MT5 Account",
      description: "In your dashboard, enter your MT5 account number, broker server, and password so we can connect your account.",
    },
    {
      icon: <Clock className="w-8 h-8 text-[#FFC107]" />,
      title: "Step 3: Wait for Verification",
      description: "Our team verifies your MT5 connection. You'll be notified once your account is approved and ready to trade.",
    },
    {
      icon: <Sliders className="w-8 h-8 text-[#E040FB]" />,
      title: "Step 4: Configure Risk Settings",
      description: "Set your risk per trade and risk per day in dashboard settings to match your trading style and account size.",
    },
    {
      icon: <ToggleRight className="w-8 h-8 text-[#007AFF]" />,
      title: "Step 5: Turn On Automatic Trading",
      description: "In your dashboard, flip the automatic trading switch to enable Alphabot. No EA file download or chart setup required.",
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-[#00E676]" />,
      title: "Step 6: Bot Trades Automatically",
      description: "Once live, Alphabot analyzes markets and executes trades 24/7 based on your configured risk settings.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      <div className="pt-24 pb-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              How It Works
            </h1>
            <p className="text-lg text-[#A1A1AA]">
              Get started in 6 simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-20">
            {steps.map((step, idx) => (
              <div key={idx} data-testid={`step-${idx + 1}`} className="relative">
                <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 card-hover h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-16 h-16 bg-[#161616] rounded-lg flex items-center justify-center shrink-0">
                      {step.icon}
                    </div>
                    <span className="text-xs font-bold text-[#52525B] uppercase tracking-wider">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-3">{step.title}</h3>
                  <p className="text-[#A1A1AA] text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Dashboard setup highlight */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 sm:p-8 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <Settings className="w-6 h-6 text-[#00E676]" />
              <h2 className="text-xl sm:text-2xl font-bold">Dashboard Setup Checklist</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "MT5 account number",
                "Broker server name",
                "MT5 account password",
                "Risk per trade (%)",
                "Risk per day (%)",
                "Automatic trading switch enabled",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 text-[#A1A1AA] text-sm sm:text-base">
                  <CheckCircle className="w-4 h-4 text-[#00E676] shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* System Requirements */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 sm:p-8 mb-12">
            <h2 className="text-2xl font-bold mb-6">System Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-[#00E676]" />
                  MT5 Platform
                </h3>
                <ul className="space-y-2 text-[#A1A1AA]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    MetaTrader 5 build 3200 or higher
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    Compatible with Windows/Mac/Linux
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    Minimum 4GB RAM recommended
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-bold mb-3 flex items-center gap-2">
                  <Server className="w-5 h-5 text-[#007AFF]" />
                  Broker & VPS
                </h3>
                <ul className="space-y-2 text-[#A1A1AA]">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    Works with any MT5-compatible broker
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    VPS recommended for 24/7 uptime
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#00E676]" />
                    Low latency internet connection
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Video Placeholder */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-6 sm:p-8">
            <h2 className="text-2xl font-bold mb-6">Setup Video Tutorial</h2>
            <div className="aspect-video bg-[#161616] rounded-lg flex items-center justify-center">
              <div className="text-center">
                <PlayCircle className="w-20 h-20 text-[#00E676] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Video tutorial coming soon</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
